import React from 'react';
import { ListChecksIcon, MailIcon, PencilLineIcon, PlusIcon, XIcon } from 'lucide-react';
import { daysPastEta, getEscalation, isResolved } from '../../utils/conditions';
import type { AttentionFilter, Condition } from '../../types/conditions';
interface ConditionsHeaderProps {
  conditions: Condition[];
  attentionFilter: AttentionFilter;
  onAttentionFilterChange: (filter: AttentionFilter) => void;
  selectedCount: number;
}
const outlineBtn = 'inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-charcoal transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';
export function ConditionsHeader({
  conditions,
  attentionFilter,
  onAttentionFilterChange,
  selectedCount
}: ConditionsHeaderProps) {
  const total = conditions.length;
  const cleared = conditions.filter(c => isResolved(c.status)).length;
  const outstanding = total - cleared;
  const pct = total ? Math.round(cleared / total * 100) : 0;
  const overdue = conditions.filter(c => daysPastEta(c) > 0).length;
  const needEta = conditions.filter(c => getEscalation(c) !== 'none').length;
  return <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 py-5" data-id="element-128" data-mp="3jgr1girk81kq">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3" data-id="element-129" data-mp="3oeiq93wwr7kp">
        <Stat label="Outstanding" value={outstanding} data-id="element-130" data-mp="39i1h8jc6y51b" />
        <Stat label="Cleared" value={cleared} data-id="element-131" data-mp="39gi0krmmhrz0" />
        <div className="flex items-center gap-3" data-id="element-132" data-mp="3egwa6e3vh9z5">
          <ProgressRing pct={pct} data-id="element-133" data-mp="3eipfnq2r1o5k" />
          <Stat label="Complete" value={`${pct}%`} data-id="element-134" data-mp="30hd21lb29ymu" />
        </div>

        <span className="hidden h-10 w-px bg-slate-200 sm:block" aria-hidden="true" data-id="element-135" data-mp="30j7nr1u5purz" />

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by attention needed" data-id="element-136" data-mp="35h995tf2266m">
          <FilterChip count={overdue} label="past ETA" active={attentionFilter === 'overdue'} tone="red" onClick={() => onAttentionFilterChange(attentionFilter === 'overdue' ? 'all' : 'overdue')} data-id="element-137" data-mp="yqrti9ailsi4" />
          <FilterChip count={needEta} label="need ETA" active={attentionFilter === 'escalated'} tone="amber" onClick={() => onAttentionFilterChange(attentionFilter === 'escalated' ? 'all' : 'escalated')} data-id="element-138" data-mp="x7rtxh3ei5vw" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-id="element-139" data-mp="1kxdua7vier5h">
        {selectedCount > 0 && <button type="button" className={outlineBtn} data-id="element-140" data-mp="1fz2m01kv4opd">
            <PencilLineIcon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-141" data-mp="1g1orzws3uw5h" />
            Mass update ({selectedCount})
          </button>}
        <button type="button" className={outlineBtn} data-id="element-142" data-mp="168q12894ytip">
          <ListChecksIcon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-143" data-mp="23owvc95z3v4v" />
          Create tasks
        </button>
        <button type="button" className={outlineBtn} data-id="element-144" data-mp="1yxonvio7ejuc">
          <PlusIcon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-145" data-mp="1yw3rdnibwqtu" />
          Add condition
        </button>
        <button type="button" className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-146" data-mp="2b8fvhs7cm50o">
          <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-147" data-mp="2l4981bl2hyig" />
          Send conditions
        </button>
      </div>
    </header>;
}
function Stat({
  label,
  value
}: {
  label: string;
  value: number | string;
}) {
  return <div data-id="element-148" data-mp="2yxgxawloehwz">
      <p className="text-xs text-slate-500" data-id="element-149" data-mp="2yxqnak6q0p2o">{label}</p>
      <p className="text-2xl font-medium tabular-nums leading-tight text-charcoal" data-id="element-150" data-mp="2z03dfkmmu5qt">{value}</p>
    </div>;
}
function ProgressRing({
  pct
}: {
  pct: number;
}) {
  const r = 15;
  const c = 2 * Math.PI * r;
  return <svg width="38" height="38" viewBox="0 0 38 38" role="img" aria-label={`${pct}% complete`} className="-rotate-90" data-id="element-151" data-mp="3cxrqw7edealu">
      <circle cx="19" cy="19" r={r} fill="none" strokeWidth="4" className="stroke-slate-100" data-id="element-152" data-mp="egqrjb6cyc19" />
      <circle cx="19" cy="19" r={r} fill="none" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} className="stroke-steel transition-[stroke-dashoffset] duration-500" data-id="element-153" data-mp="9ip7oo13y8oq" />
    </svg>;
}
interface FilterChipProps {
  count: number;
  label: string;
  active: boolean;
  tone: 'red' | 'amber';
  onClick: () => void;
}
function FilterChip({
  count,
  label,
  active,
  tone,
  onClick
}: FilterChipProps) {
  if (count === 0 && !active) return null;
  const styles = tone === 'red' ? {
    chip: 'bg-red-50 text-red-600 hover:bg-red-100',
    ring: 'ring-red-400',
    dot: 'bg-red-500'
  } : {
    chip: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    ring: 'ring-amber-400',
    dot: 'bg-amber-500'
  };
  return <button type="button" aria-pressed={active} onClick={onClick} title={active ? 'Clear filter' : `Show only conditions ${label}`} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${styles.chip} ${active ? `ring-2 ring-inset ${styles.ring}` : ''}`} data-id="element-154" data-mp="1ayd5vdxq3zdy">
      <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} aria-hidden="true" data-id="element-155" data-mp="1ehcatyy35fq9" />
      {count} {label}
      {active && <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-156" data-mp="2290yv33vji1e" />}
    </button>;
}